/**
 * Standard question text map
 *
 * Maps option type value → a function that returns the question string shown
 * to the user in the assistant chatbot.  The function receives an optional
 * frame index (1, 2, 3 …) for per-frame questions.
 *
 * All questions are written from the perspective of helping a customer
 * configure their window order.  Edit entries here to adjust wording without
 * touching any question-generation logic.
 */

export type QuestionTemplateFn = (
  frameIndex?: number,
  categoryName?: string,
) => string;

const f = (i?: number) => (i ? ` for Frame ${i}` : "");

export const OPTION_TYPE_QUESTION_MAP: Record<number, QuestionTemplateFn> = {
  // ── Window Type ───────────────────────────────────────────────────────────
  2: () => "Would you require Deep Bottom Rail?",
  3: () => "Would you require  Run Through Horns?",
  72: () => "What type of coupling clip would you like on your window?",

  // ── Measurements ─────────────────────────────────────────────────────────
  4: (i) => `Please enter the dimension for your window${f(i)}`,
  5: () => "What is the transom drop measurement for your window?",
  58: (i) =>
    `Please enter the enter transom drop for your window in numeric values (mm)${f(i)}`,
  59: (i) => `Please enter the custom fraction measurement${f(i)}`,

  // ── Sash ─────────────────────────────────────────────────────────────────
  7: (i) => `How would you like your sashes configured${f(i)}?`,
  23: (i) => `What style would you like for the bottom sash${f(i)}?`,
  24: (i) => `What style would you like for the top sash${f(i)}?`,
  49: (_i, cat) => {
    const c = (cat ?? "").toLowerCase();
    const subject = c.includes("sash")
      ? "your sashes"
      : c.includes("glaz")
        ? "your glazing"
        : c.includes("trickle")
          ? "your trickle vents"
          : c.includes("decorative")
            ? "your decorative bars"
            : c.includes("colour") ||
                c.includes("color") ||
                c.includes("profile")
              ? "the profile colours"
              : c.includes("hardware")
                ? "the hardware"
                : c.includes("cill")
                  ? "the cill"
                  : "your frames";
    return `How would you like to configure ${subject} — the same for all frames or individually?`;
  },

  // ── Profile Colours ───────────────────────────────────────────────────────
  10: () => "Would you like a spray finish on your window?",
  11: () => "Which side of the window would you like sprayed?",
  12: () => "What colour would you like for the inside spray?",
  13: () => "What colour would you like for the outside spray?",
  34: () => "What external colour would you like for your window?",
  35: () => "What internal colour would you like for your window?",
  60: () => "What type of profile colour would you like for your window?",
  71: () => "Would you like a putty line finish on your window?",

  // ── Hardware ─────────────────────────────────────────────────────────────
  9: (i) => `What colour would you like for your window hardware${f(i)}?`,
  18: (i) => `Which hardware would you like on your window${f(i)}?`,
  61: (i) => `What type of hardware would you like on your window${f(i)}?`,
  66: () => "Where would you like the D-handle positioned on your window?",
  76: () => "Please add any additional notes for the hardware",

  // ── Cill ─────────────────────────────────────────────────────────────────
  21: (i) => `Which cill size would you like for your window${f(i)}?`,
  15: (i) => `Would you like a cill extension fitted to your window${f(i)}?`,
  16: (i) => `Would you like cill horns on your window${f(i)}?`,
  17: (i) => `Would you like a welded cill return on your window${f(i)}?`,
  53: (i) => `What size cill would you like${f(i)}?`,
  57: (i) => `How many cills do you require${f(i)}?`,
  73: (i) => `Would you like angled cill horns on your window${f(i)}?`,
  74: (i) => `What size angled cill horn would you like${f(i)}?`,
  77: (i) => `What size cill horn would you like${f(i)}?`,
  78: (i) => `How many welded cill returns do you need${f(i)}?`,

  // ── Security ─────────────────────────────────────────────────────────────
  22: () => "What security option would you like on your window?",

  // ── Glazing ──────────────────────────────────────────────────────────────
  28: (i) => `What glass specification would you like for your window${f(i)}?`,
  29: () => "What colour space bar would you like in your glazing?",
  31: (i) => `What glazing pattern would you like for your window${f(i)}?`,
  92: (i) => `What glazing pattern would you like for the top sash${f(i)}?`,
  93: (i) => `What glazing pattern would you like for the bottom sash${f(i)}?`,
  41: () => "Would you like obscure glazing on your window?",
  42: (i) => `Where would you like the obscure glazing placed${f(i)}?`,
  62: () => "What type of glazing would you like for your window?",
  81: (i) => `What glass type would you like for your window${f(i)}?`,

  // ── Decorative Bars ───────────────────────────────────────────────────────
  36: (i) => `What lead work would you like on the top sash${f(i)}?`,
  37: (i) => `What lead work would you like on the bottom sash${f(i)}?`,
  38: () => "What style would you like for the arch top sash?",
  39: () => "What style would you like for the arch bottom sash?",
  63: () => "What type of decorative bars would you like on your window?",
  75: () => "Please add any additional notes for the decorative bars",

  // ── Trickle Vents ────────────────────────────────────────────────────────
  40: (i) => `Would you like trickle vents on your window${f(i)}?`,
  26: (i) =>
    `Which trickle vent airflow would you like placed in the head${f(i)}?`,
  27: (i) =>
    `Which trickle vent airflow would you like placed in the top sash${f(i)}?`,
  79: (i) => `Where would you like the trickle vents positioned${f(i)}?`,

  // ── Add Ons ──────────────────────────────────────────────────────────────
  83: (i) => `Is the add-on included in the window sizes or extra to the sizes${f(i)}?`,
  85: (i) =>
    `Which face of the window would you like the head extension fitted to${f(i)}?`,
  86: (i) => `Which face of the window would you like the cill fitted to${f(i)}?`,
  87: (i) =>
    `Which face of the window would you like the left jamb extension fitted to${f(i)}?`,
  88: (i) =>
    `Which face of the window would you like the right jamb extension fitted to${f(i)}?`,
  44: (i) => `Would you like a head extension on your window${f(i)}?`,
  45: (i) => `Would you like a left jamb extension on your window${f(i)}?`,
  46: (i) => `Would you like a right jamb extension on your window${f(i)}?`,
  50: (i) => `What size head extension would you like${f(i)}?`,
  51: (i) => `What size left jamb extension would you like${f(i)}?`,
  52: (i) => `What size right jamb extension would you like${f(i)}?`,
  54: (i) => `How many head extensions do you require${f(i)}?`,
  55: (i) => `How many left jamb extensions do you require${f(i)}?`,
  56: (i) => `How many right jamb extensions do you require${f(i)}?`,
  80: (i) => `Which face of the window would you like the add-on fitted to${f(i)}?`,
  82: (i) => `Do you require Add Ons on your window${f(i)}?`,

  // ── Optional Extras ──────────────────────────────────────────────────────
  47: () => "Would you like a weather bar on your window?",
  48: () => "Would you like to check fire egress compliance for your window?",
  84: () => "How many fixing lugs would you like?",
  89: () => "What fitting type would you like for your window?",

  // ── Frame count ───────────────────────────────────────────────────────────
  64: () => "How many frames would you like for your window?",
};

/**
 * Optional helper text shown beneath a question.
 * Keyed by option type value — add an entry here to surface guidance text
 * without changing the question itself.
 */
export const OPTION_TYPE_HELPER_TEXT_MAP: Record<number, string> = {
  4:  "Measurement should be entered left to right",
  21: "The colour for the cill will be the same as the profile colour.",
  58: "In mm. Top & Bottom Sash must not exceed the total window height.",
  59: "Top & Bottom Fraction values must not exceed 100% combined.",
};
